import React, { useState, useEffect, useMemo } from 'react';
import { FileText, RefreshCw, Send, AlertTriangle, X, CheckCircle, Search, Check, Copy, Globe } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip
} from '@mui/material';
import MainService from '../../services/MainService';
import SmsKeywordTextField from './SmsKeywordTextField';

const DeliveryNoteSMS = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showSelectModal, setShowSelectModal] = useState(false);
  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);

  const [dnNote, setDnNote] = useState({
    Id: 0,
    RefNo: '',
    Date: '',
    Driver: { Name: '' },
    Message: '',
    DndIds: []
  });

  const [dnDetails, setDnDetails] = useState([]);
  const [allSelected, setAllSelected] = useState(false);
  const [isEnglish, setIsEnglish] = useState(false);
  const [smsText, setSmsText] = useState({ CustomerMsgEN: '', CustomerMsgAR: '' });

  const [search, setSearch] = useState({
    Order: {
      VoucherNo: '',
      Client: { Name: '', MobileNo1: '' },
      ClientAddress: { Emirate: { Name: '' }, Location: { Name: '' } },
      Customer: { Name: '', MobileNo1: '' },
      CustomerAddress: { Emirate: { Name: '' }, Location: { Name: '' } }
    }
  });

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const init = async () => {
    setDnNote({
      Id: 0,
      RefNo: '',
      Date: '',
      Driver: { Name: '' },
      Message: '',
      DndIds: []
    });
    setDnDetails([]);
    setAllSelected(false);
    setIsEnglish(false);
    setSearch({
      Order: {
        VoucherNo: '',
        Client: { Name: '', MobileNo1: '' },
        ClientAddress: { Emirate: { Name: '' }, Location: { Name: '' } },
        Customer: { Name: '', MobileNo1: '' },
        CustomerAddress: { Emirate: { Name: '' }, Location: { Name: '' } }
      }
    });

    try {
      const response = await MainService.getDeliveryNoteMessageText();
      if (response) {
        setSmsText({
          CustomerMsgEN: response.CustomerMsgEN || '',
          CustomerMsgAR: response.CustomerMsgAR || ''
        });
        setDnNote(prev => ({ ...prev, Message: response.CustomerMsgAR || '' }));
      }
    } catch (err) {
      console.error('Error loading message text:', err);
    }
  };

  const loadDeliveryNotes = async () => {
    try {
      setLoadingNotes(true);
      const response = await MainService.DeliveryNotesInGrid({
        filter: {
          filters: [{ field: 'Status', operator: 'eq', value: 'CONFIRMED' }],
          logic: 'and'
        }
      });
      
      if (response && response.Data) {
        setDeliveryNotes(response.Data);
      } else if (Array.isArray(response)) {
        setDeliveryNotes(response);
      } else {
        setDeliveryNotes([]);
      }
    } catch (err) {
      console.error('Error loading delivery notes:', err);
      setError('Failed to load delivery notes');
      setDeliveryNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  };

  // Compute filtered details using useMemo
  const filteredDetails = useMemo(() => {
    return dnDetails.filter(detail => {
      const order = detail.Order || {};
      const client = order.Client || {};
      const customer = order.Customer || {};
      const clientAddress = order.ClientAddress || {};
      const customerAddress = order.CustomerAddress || {};

      const matches =
        (!search.Order.VoucherNo || (order.VoucherNo || '').toLowerCase().includes(search.Order.VoucherNo.toLowerCase())) &&
        (!search.Order.Client.Name || (client.Name || '').toLowerCase().includes(search.Order.Client.Name.toLowerCase())) &&
        (!search.Order.Client.MobileNo1 || (client.MobileNo1 || '').includes(search.Order.Client.MobileNo1)) &&
        (!search.Order.ClientAddress.Emirate.Name || (clientAddress.Emirate?.Name || '').toLowerCase().includes(search.Order.ClientAddress.Emirate.Name.toLowerCase())) &&
        (!search.Order.ClientAddress.Location.Name || (clientAddress.Location?.Name || '').toLowerCase().includes(search.Order.ClientAddress.Location.Name.toLowerCase())) &&
        (!search.Order.Customer.Name || (customer.Name || customer.FullName || '').toLowerCase().includes(search.Order.Customer.Name.toLowerCase())) &&
        (!search.Order.Customer.MobileNo1 || (customer.MobileNo1 || '').includes(search.Order.Customer.MobileNo1)) &&
        (!search.Order.CustomerAddress.Emirate.Name || (customerAddress.Emirate?.Name || '').toLowerCase().includes(search.Order.CustomerAddress.Emirate.Name.toLowerCase())) &&
        (!search.Order.CustomerAddress.Location.Name || (customerAddress.Location?.Name || '').toLowerCase().includes(search.Order.CustomerAddress.Location.Name.toLowerCase()));

      return matches;
    });
  }, [dnDetails, search]);

  useEffect(() => {
    if (filteredDetails.length > 0) {
      const allSelectedNow = filteredDetails.every(d => d.IsSelected);
      setAllSelected(allSelectedNow);
    } else {
      setAllSelected(false);
    }
  }, [filteredDetails]);

  const handleSelectDeliveryNote = async (note) => {
    setDnNote({
      Id: note.Id,
      RefNo: note.RefNo || '',
      Date: note.Date || '',
      Driver: note.Driver || { Name: '' },
      Message: smsText.CustomerMsgAR || '',
      DndIds: []
    });
    setShowSelectModal(false);
    setDnDetails([]);
    setAllSelected(false);

    try {
      setLoading(true);
      const response = await MainService.GetDeliveryNoteById(note.Id);
      if (response && response.DeliveryNoteDetails) {
        const details = response.DeliveryNoteDetails.map(d => ({ ...d, IsSelected: false }));
        setDnDetails(details);
      } else {
        setError('Error fetching delivery note details');
      }
    } catch (err) {
      console.error('Error loading delivery note details:', err);
      setError('Error fetching delivery note details');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    const newAllSelected = !allSelected;
    setAllSelected(newAllSelected);
    // Select/deselect only filtered items
    const filteredIds = new Set(filteredDetails.map(d => d.Id));
    setDnDetails(prev => prev.map(d => 
      filteredIds.has(d.Id) ? { ...d, IsSelected: newAllSelected } : d
    ));
  };

  const handleSelectOrder = (detail) => {
    setDnDetails(prev => {
      const updated = prev.map(d => 
        d.Id === detail.Id ? { ...d, IsSelected: !d.IsSelected } : d
      );
      return updated;
    });
  };

  const handleSwitchLanguage = () => {
    const newIsEnglish = !isEnglish;
    setIsEnglish(newIsEnglish);
    setDnNote(prev => ({
      ...prev,
      Message: newIsEnglish ? smsText.CustomerMsgEN : smsText.CustomerMsgAR
    }));
  };

  const handleCopyKeyword = (keyword) => {
    navigator.clipboard.writeText(keyword);
    setSuccess(`Copied ${keyword} to clipboard`);
  };

  const handleSendSMS = async () => {
    if (!dnNote.Message.trim()) {
      setError('Message is empty');
      return;
    }

    const selectedIds = dnDetails.filter(d => d.IsSelected).map(d => d.Id);
    if (selectedIds.length === 0) {
      setError('No details selected');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const payload = {
        ...dnNote,
        DndIds: selectedIds
      };

      const response = await MainService.SendDirectDeliveryNoteSms(payload);
      
      if (response === 'Success' || response === true) {
        setSuccess('SMS sent successfully');
        init();
      } else {
        setError(response || 'Error sending SMS');
      }
    } catch (err) {
      console.error('Error sending SMS:', err);
      setError('Error sending SMS');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return dateString;
    }
  };

  const keywords = [
    { name: 'Voucher No', keyword: '@VoucherNo' },
    { name: 'Driver Name', keyword: '@DriverName' },
    { name: 'Driver Phone No', keyword: '@DriverPhoneNo' },
    { name: 'OTP', keyword: '@OTP' },
    { name: 'Delivery Date', keyword: '@DeliveryDate' },
    { name: 'Status', keyword: '@Status' },
    { name: 'Money', keyword: '@Money' },
    { name: 'Order Count', keyword: '@OrderCount' },
    { name: 'Salutation', keyword: '@Name' },
    { name: 'New Line', keyword: '%0A' },
    { name: 'Reason', keyword: '@Reason' },
    { name: 'Client Name', keyword: '@ClientName' },
    { name: 'Customer Name', keyword: '@CustomerName' },
    { name: 'Customer Amount', keyword: '@CustomerMoney' },
    { name: 'Link', keyword: '@LINK' },
    { name: 'Client Amount', keyword: '@ClientMoney' },
    { name: 'Client Area', keyword: '@ClientArea' },
    { name: 'Client Location', keyword: '@ClientLoc' },
    { name: 'Client Emirate', keyword: '@ClientEmirate' },
    { name: 'Customer Area', keyword: '@CustomerArea' },
    { name: 'Customer Location', keyword: '@CustomerLocation' },
    { name: 'Customer Emirate', keyword: '@CustomerEmirate' }
  ];

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-6 pb-4 border-b border-gray-200">
          <FileText className="w-6 h-6 text-[#46955A] mr-2" />
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Delivery Note SMS</h1>
            <p className="text-sm text-gray-500 mt-1">Send SMS for delivery notes</p>
          </div>
        </div>

        {/* Select Delivery Note Button */}
        <div className="mb-6">
          <Button
            variant="outlined"
            startIcon={<Search className="w-4 h-4" />}
            onClick={() => {
              setShowSelectModal(true);
              loadDeliveryNotes();
            }}
            sx={{
              borderColor: '#46955A',
              color: '#46955A',
              '&:hover': { borderColor: '#3d7d4f', backgroundColor: '#f0f9f4' }
            }}
          >
            Select Delivery Note
          </Button>
        </div>

        {/* Selected Delivery Note Info */}
        {dnNote.Id > 0 && (
          <div className="mb-6">
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: '8px' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>DN No</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Driver</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '12px' }}>
                      {dnNote.RefNo || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '12px' }}>
                      {formatDate(dnNote.Date)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '12px' }}>
                      {dnNote.Driver?.Name || 'N/A'}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        )}

        {/* Filter Section */}
        {dnNote.Id > 0 && (
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Filter Result</h4>
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: '8px' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Voucher No</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Client</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Mobile No</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Emirate</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Location</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ padding: '8px' }}>
                      <TextField
                        size="small"
                        fullWidth
                        value={search.Order.VoucherNo}
                        onChange={(e) => setSearch(prev => ({
                          ...prev,
                          Order: { ...prev.Order, VoucherNo: e.target.value }
                        }))}
                        placeholder="Voucher No"
                      />
                    </TableCell>
                    <TableCell sx={{ padding: '8px' }}>
                      <TextField
                        size="small"
                        fullWidth
                        value={search.Order.Client.Name}
                        onChange={(e) => setSearch(prev => ({
                          ...prev,
                          Order: {
                            ...prev.Order,
                            Client: { ...prev.Order.Client, Name: e.target.value }
                          }
                        }))}
                        placeholder="Client Name"
                      />
                    </TableCell>
                    <TableCell sx={{ padding: '8px' }}>
                      <TextField
                        size="small"
                        fullWidth
                        value={search.Order.Client.MobileNo1}
                        onChange={(e) => setSearch(prev => ({
                          ...prev,
                          Order: {
                            ...prev.Order,
                            Client: { ...prev.Order.Client, MobileNo1: e.target.value }
                          }
                        }))}
                        placeholder="Mobile No"
                      />
                    </TableCell>
                    <TableCell sx={{ padding: '8px' }}>
                      <TextField
                        size="small"
                        fullWidth
                        value={search.Order.ClientAddress.Emirate.Name}
                        onChange={(e) => setSearch(prev => ({
                          ...prev,
                          Order: {
                            ...prev.Order,
                            ClientAddress: {
                              ...prev.Order.ClientAddress,
                              Emirate: { Name: e.target.value }
                            }
                          }
                        }))}
                        placeholder="Emirate"
                      />
                    </TableCell>
                    <TableCell sx={{ padding: '8px' }}>
                      <TextField
                        size="small"
                        fullWidth
                        value={search.Order.ClientAddress.Location.Name}
                        onChange={(e) => setSearch(prev => ({
                          ...prev,
                          Order: {
                            ...prev.Order,
                            ClientAddress: {
                              ...prev.Order.ClientAddress,
                              Location: { Name: e.target.value }
                            }
                          }
                        }))}
                        placeholder="Location"
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Customer</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Mobile No</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Emirate</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Location</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ padding: '8px' }}>
                      <TextField
                        size="small"
                        fullWidth
                        value={search.Order.Customer.Name}
                        onChange={(e) => setSearch(prev => ({
                          ...prev,
                          Order: {
                            ...prev.Order,
                            Customer: { ...prev.Order.Customer, Name: e.target.value }
                          }
                        }))}
                        placeholder="Customer Name"
                      />
                    </TableCell>
                    <TableCell sx={{ padding: '8px' }}>
                      <TextField
                        size="small"
                        fullWidth
                        value={search.Order.Customer.MobileNo1}
                        onChange={(e) => setSearch(prev => ({
                          ...prev,
                          Order: {
                            ...prev.Order,
                            Customer: { ...prev.Order.Customer, MobileNo1: e.target.value }
                          }
                        }))}
                        placeholder="Mobile No"
                      />
                    </TableCell>
                    <TableCell sx={{ padding: '8px' }}>
                      <TextField
                        size="small"
                        fullWidth
                        value={search.Order.CustomerAddress.Emirate.Name}
                        onChange={(e) => setSearch(prev => ({
                          ...prev,
                          Order: {
                            ...prev.Order,
                            CustomerAddress: {
                              ...prev.Order.CustomerAddress,
                              Emirate: { Name: e.target.value }
                            }
                          }
                        }))}
                        placeholder="Emirate"
                      />
                    </TableCell>
                    <TableCell sx={{ padding: '8px' }}>
                      <TextField
                        size="small"
                        fullWidth
                        value={search.Order.CustomerAddress.Location.Name}
                        onChange={(e) => setSearch(prev => ({
                          ...prev,
                          Order: {
                            ...prev.Order,
                            CustomerAddress: {
                              ...prev.Order.CustomerAddress,
                              Location: { Name: e.target.value }
                            }
                          }
                        }))}
                        placeholder="Location"
                      />
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        )}

        {/* Loading Indicator */}
        {loading && (
          <div className="text-center py-4">
            <CircularProgress size={32} sx={{ color: '#46955A' }} />
            {dnNote.Id > 0 && (
              <p className="text-sm text-gray-600 mt-2">Loading details of {dnNote.RefNo}</p>
            )}
          </div>
        )}

        {/* Delivery Note Details Table */}
        {dnNote.Id > 0 && !loading && (
          <div className="mb-6">
            <div className="mb-4">
              <Button
                variant="outlined"
                onClick={handleSelectAll}
                sx={{
                  borderColor: '#46955A',
                  color: '#46955A',
                  '&:hover': { borderColor: '#3d7d4f', backgroundColor: '#f0f9f4' }
                }}
              >
                {allSelected ? 'Deselect All' : 'Select All'}
              </Button>
            </div>

            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: '8px', maxHeight: '400px', overflow: 'auto' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px', width: '40px' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px', width: '80px' }}>Select</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Voucher No</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Delivery Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Client</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Client Location</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Customer</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Customer Location</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDetails.length > 0 ? (
                    filteredDetails.map((detail, index) => {
                      const order = detail.Order || {};
                      const client = order.Client || {};
                      const customer = order.Customer || {};
                      const clientAddress = order.ClientAddress || {};
                      const customerAddress = order.CustomerAddress || {};

                      return (
                        <TableRow
                          key={detail.Id}
                          hover
                          sx={{
                            backgroundColor: detail.IsSelected ? '#f0f9f4' : 'transparent'
                          }}
                        >
                          <TableCell sx={{ fontSize: '0.875rem', color: '#6B7280', padding: '12px' }}>
                            {index + 1}
                          </TableCell>
                          <TableCell sx={{ padding: '12px' }}>
                            <IconButton
                              size="small"
                              onClick={() => handleSelectOrder(detail)}
                              sx={{
                                color: detail.IsSelected ? '#46955A' : '#9CA3AF',
                                '&:hover': { backgroundColor: '#f0f9f4' }
                              }}
                            >
                              <Check className={detail.IsSelected ? 'w-5 h-5' : 'w-4 h-4'} />
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#111827', padding: '12px' }}>
                            {order.VoucherNo || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '12px' }}>
                            {formatDate(order.DeliveryDate)}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '12px' }}>
                            <div>
                              <div>{client.Name || 'N/A'}</div>
                              <div className="text-xs text-gray-500">{client.MobileNo1 || ''}</div>
                            </div>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '12px' }}>
                            {clientAddress.Location?.Name || ''}, {clientAddress.Emirate?.Name || ''}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '12px' }}>
                            <div>
                              <div>{customer.FullName || customer.Name || 'N/A'}</div>
                              <div className="text-xs text-gray-500">{customer.MobileNo1 || ''}</div>
                            </div>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '12px' }}>
                            {customerAddress.Location?.Name || ''}, {customerAddress.Emirate?.Name || ''}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ padding: '40px' }}>
                        <div className="flex flex-col items-center justify-center text-gray-400">
                          <FileText className="w-12 h-12 mb-3 opacity-50" />
                          <p className="text-sm font-medium">No delivery note details found</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        )}

        {/* Draft SMS Section */}
        {dnNote.Id > 0 && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Draft SMS</label>
            <SmsKeywordTextField
              fullWidth
              multiline
              rows={5}
              value={dnNote.Message}
              onValueChange={(value) => setDnNote(prev => ({ ...prev, Message: value }))}
              placeholder="Draft your SMS message here..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#46955A',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#46955A',
                  },
                },
              }}
            />
            <div className="mt-2">
              <Button
                variant="outlined"
                startIcon={<Globe className="w-4 h-4" />}
                onClick={handleSwitchLanguage}
                size="small"
                sx={{
                  borderColor: '#46955A',
                  color: '#46955A',
                  '&:hover': { borderColor: '#3d7d4f', backgroundColor: '#f0f9f4' }
                }}
              >
                {isEnglish ? 'Switch to Arabic' : 'Switch to English'}
              </Button>
            </div>
          </div>
        )}

        {/* Keyword Reference */}
        {dnNote.Id > 0 && (
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Keyword Reference</h4>
            <p className="text-sm text-gray-600 mb-4">Click on any keyword to copy it to clipboard</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {keywords.map((kw, index) => (
                <div
                  key={index}
                  className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-[#46955A] transition-colors cursor-pointer"
                  onClick={() => handleCopyKeyword(kw.keyword)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{kw.name}</p>
                      <p className="text-xs text-gray-500 mt-1 font-mono">{kw.keyword}</p>
                    </div>
                    <Tooltip title="Copy">
                      <IconButton size="small" sx={{ color: '#46955A' }}>
                        <Copy className="w-4 h-4" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-center gap-3 mt-6">
          <Button
            variant="outlined"
            startIcon={<RefreshCw className="w-4 h-4" />}
            onClick={init}
            disabled={loading}
            sx={{
              borderColor: '#46955A',
              color: '#46955A',
              '&:hover': { borderColor: '#3d7d4f', backgroundColor: '#f0f9f4' },
              '&:disabled': { borderColor: '#9CA3AF', color: '#9CA3AF' }
            }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <Send className="w-4 h-4" />}
            onClick={handleSendSMS}
            disabled={loading || dnNote.Id === 0}
            sx={{
              backgroundColor: '#46955A',
              '&:hover': { backgroundColor: '#3d7d4f' },
              '&:disabled': { backgroundColor: '#9CA3AF' }
            }}
          >
            Send SMS
          </Button>
        </div>
      </div>

      {/* Select Delivery Note Modal */}
      <Dialog
        open={showSelectModal}
        onClose={() => setShowSelectModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ color: '#111827', fontWeight: 600 }}>
          Select Delivery Note
        </DialogTitle>
        <DialogContent>
          {loadingNotes ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress size={32} sx={{ color: '#46955A' }} />
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: '8px', maxHeight: '400px', overflow: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Ref No</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Driver</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deliveryNotes.length > 0 ? (
                    deliveryNotes.map((note) => (
                      <TableRow key={note.Id} hover>
                        <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '12px' }}>
                          {note.RefNo || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '12px' }}>
                          {formatDate(note.Date)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '12px' }}>
                          {note.Driver?.Name || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ padding: '12px' }}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleSelectDeliveryNote(note)}
                            sx={{
                              backgroundColor: '#46955A',
                              '&:hover': { backgroundColor: '#3d7d4f' }
                            }}
                          >
                            Select
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ padding: '40px' }}>
                        <div className="flex flex-col items-center justify-center text-gray-400">
                          <FileText className="w-12 h-12 mb-3 opacity-50" />
                          <p className="text-sm font-medium">No delivery notes found</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSelectModal(false)} sx={{ color: '#6B7280' }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DeliveryNoteSMS;

